import { AgGridChoiceFilterAdapter, AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import { UerStatus } from 'constantsProject';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import type { PriceListUerRowData, PriceListUerRowDataFilterInput, UerRecordStatusEnum } from 'gql/graphql';
import { PriceConstructionTypeRelationStatusEnum } from 'gql/graphql';
import { useAgGridDataSource } from 'hooks/useAgGridDataSource';
import type { AnyObject } from 'types/AnyObject';
import { Checkbox } from 'uikit';
import { createWhereVariablesGetter } from 'utils/getWhereVariables';
import { getYesNoStatus } from 'utils/getYesNoTableStatus';

import type { ColDef, ColGroupDef, ValueFormatterParams } from 'ag-grid-community';
import debounce from 'debounce';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
    getAgreementUerForIncluding,
    uerContractChangeUerForIncludingNameFilter,
    getContractChangeUerForIncludingCodeFilter,
    getContractChangeUerForIncludingLinkStatusFilter,
    getContractChangeUerForIncludingStatusFilter,
    getContractChangeUerForIncludingUnitFilter,
    getContractChangeUerForIncludingSectionNameFilter,
    getContractChangeUerForIncludingGroupFullNameFilter,
} from '../AgreementsQueries';

const TITLE = 'Добавление расценки из прейскуранта расценок';
const SEARCH_PLACEHOLDER = 'Поиск расценки в прейскуранте';

const StatusLink = {
    [PriceConstructionTypeRelationStatusEnum.AdditionalCondition]: 'Условие ДС',
    [PriceConstructionTypeRelationStatusEnum.ContractCondition]: 'Условие договора',
    [PriceConstructionTypeRelationStatusEnum.NewRecord]: 'Новая связь',
};

const getWhereVariables = createWhereVariablesGetter<PriceListUerRowDataFilterInput | undefined>({
    searchStringFields: ['code', 'name', 'workScopes'],
    customSearchStringFieldCreators: {
        workScopes: (value: string) => ({
            some: {
                description: {
                    containsi: value,
                },
            },
        }),
    },
    filtersToWhere: (selectedFilters: [string, boolean[] | string[]][]) =>
        selectedFilters.map(([fieldName, value]) => {
            if (
                fieldName === 'isIncludedInWorkOrder' ||
                fieldName === 'isIncludedInAct' ||
                fieldName === 'isIncludedInWorkOrderChange'
            ) {
                return {
                    [fieldName]: {
                        eq: value[0] === 'Да',
                    },
                };
            }

            if (fieldName === 'groupName') {
                return {
                    groupFullName: {
                        in: value,
                    },
                };
            }

            return {
                [fieldName]: {
                    in: value,
                },
            };
        }),
});

export const useAgreementTableAddUer = ({ isModalOpened }: { isModalOpened: boolean }) => {
    const { agreementId: agreementIdString } = useParams();
    const agreementId = Number(agreementIdString);

    const [queryWhere, setQueryWhere] = useState<any>(undefined);
    const [checkedIds, setCheckedIds] = useState<Record<string, boolean>>({});
    const [searchString, setSearchString] = useState('');
    const [isSubmittedDataTable, setIsSubmittedDataTable] = useState<boolean>(false);

    const variables = useMemo(
        () => ({
            contractChangeId: agreementId,
            where: getWhereVariables(queryWhere),
        }),
        [agreementId, queryWhere],
    );

    const [
        agreementUerForIncludingData,
        agreementUerForIncludingStatus,
        agreementUerForIncludingErr,
        agreementUerForIncludingRequest,
    ] = useRequest({
        requestId: 'getAgreementUerForIncluding',
        query: getAgreementUerForIncluding,
        withEffect: false,
    });

    const { dataSource, getRowParams } = useAgGridDataSource({
        requestId: 'getAgreementUerForIncluding',
        requestFn: agreementUerForIncludingRequest,
        requestVariables: variables,
    });

    useEffect(() => {
        if (!isModalOpened) {
            setCheckedIds({});
        }
    }, [isModalOpened, variables]);

    const handleChangeCheckbox = useCallback(
        (rowId: number) =>
            ({ checked }: { checked: boolean }) => {
                setCheckedIds((ids) => ({ ...ids, [rowId]: checked }));
            },
        [setCheckedIds],
    );

    const handleTableFilterUpdated = useCallback(
        (value: { checkedValues: string[]; fieldName: string; fullDataSet: { data: { name: string } }[] }) => {
            setQueryWhere((prevFilters: any) => ({ ...prevFilters, [value.fieldName]: value.checkedValues }));
        },
        [],
    );

    const filterChangeHandler = useCallback((value: { checkedValues: string[]; fieldName: string }) => {
        setQueryWhere((prevFilters: any) => ({ ...prevFilters, [value.fieldName]: value.checkedValues }));
    }, []);

    const columns = useMemo<(ColDef<PriceListUerRowData> | ColGroupDef<PriceListUerRowData>)[]>(
        () => [
            {
                headerName: '',
                field: 'menu' as any,
                lockPosition: 'left',
                width: 55,
                cellRenderer: ({ data }: { data: PriceListUerRowData }) => {
                    if (!data) {
                        return null;
                    }

                    return (
                        <Checkbox
                            checked={checkedIds[data.id]}
                            onChange={handleChangeCheckbox(data.id)}
                            style={{ marginTop: 4 }}
                        />
                    );
                },
            },
            {
                field: 'sectionName',
                headerName: 'Раздел',
                width: 140,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables: { contractChangeId: agreementId },
                    changeFilterHandler: filterChangeHandler,
                    dataAccessor: 'uerContractChangeUerForIncludingSectionNameFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Раздел',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    query: getContractChangeUerForIncludingSectionNameFilter,
                },
            },
            {
                field: 'groupName',
                headerName: 'Подраздел',
                width: 140,
                valueFormatter: (params: ValueFormatterParams) => {
                    const { data } = params;

                    return data ? `${data?.groupCode} ${data?.groupName}` : '';
                },
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables: { contractChangeId: agreementId },
                    changeFilterHandler: filterChangeHandler,
                    dataAccessor: 'uerContractChangeUerForIncludingGroupFullNameFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Подраздел',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    query: getContractChangeUerForIncludingGroupFullNameFilter,
                },
            },
            {
                field: 'code',
                headerName: 'Шифр',
                width: 140,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables: { contractChangeId: agreementId },
                    changeFilterHandler: handleTableFilterUpdated,
                    dataAccessor: 'uerContractChangeUerForIncludingCodeFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Шифр',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    query: getContractChangeUerForIncludingCodeFilter,
                },
            },
            {
                field: 'name',
                headerName: 'Наименование',
                flex: 1,
                minWidth: 150,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables: { contractChangeId: agreementId },
                    changeFilterHandler: handleTableFilterUpdated,
                    dataAccessor: 'uerContractChangeUerForIncludingNameFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Наименование',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    query: uerContractChangeUerForIncludingNameFilter,
                },
            },
            {
                field: 'unit',
                headerName: 'Ед.изм.',
                width: 120,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables: { contractChangeId: agreementId },
                    changeFilterHandler: handleTableFilterUpdated,
                    dataAccessor: 'uerContractChangeUerForIncludingUnitFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Ед.изм.',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    query: getContractChangeUerForIncludingUnitFilter,
                },
            },
            {
                field: 'status',
                headerName: 'Статус записи',
                width: 130,
                valueFormatter: ({ value }: { value: UerRecordStatusEnum }) => UerStatus[value],
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    withSearch: false,
                    variables: { contractChangeId: agreementId },
                    changeFilterHandler: handleTableFilterUpdated,
                    dataAccessor: 'uerContractChangeUerForIncludingStatusFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Статус записи',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                            valueFormatter: ({ value }: { value: UerRecordStatusEnum }) => UerStatus[value],
                        },
                    ],
                    query: getContractChangeUerForIncludingStatusFilter,
                },
            },
            {
                field: 'linkStatus',
                headerName: 'Статус связи',
                width: 120,
                valueFormatter: ({ value }: { value: PriceConstructionTypeRelationStatusEnum }) => StatusLink[value],
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    withSearch: false,
                    variables: { contractChangeId: agreementId },
                    changeFilterHandler: handleTableFilterUpdated,
                    dataAccessor: 'uerContractChangeUerForIncludingLinkStatusFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Статус связи',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                            valueFormatter: ({ value }: { value: PriceConstructionTypeRelationStatusEnum }) =>
                                StatusLink[value],
                        },
                    ],
                    query: getContractChangeUerForIncludingLinkStatusFilter,
                },
            },
            {
                headerName: 'Используется',
                marryChildren: true,
                children: [
                    {
                        field: 'isIncludedInWorkOrder',
                        headerName: 'НЗ',
                        width: 80,
                        valueFormatter: ({ value }: { value: boolean }) => getYesNoStatus(value) ?? '',
                        filter: AgGridChoiceFilterAdapter,
                        filterParams: {
                            changeFilterHandler: handleTableFilterUpdated,
                            filterItems: [
                                {
                                    name: 'Да',
                                    value: 'Да',
                                },
                                { name: 'Нет', value: 'Нет' },
                            ],
                        },
                    },
                    {
                        field: 'isIncludedInAct',
                        headerName: 'Акт',
                        width: 85,
                        valueFormatter: ({ value }: { value: boolean }) => getYesNoStatus(value) ?? '',
                        filter: AgGridChoiceFilterAdapter,
                        filterParams: {
                            changeFilterHandler: handleTableFilterUpdated,
                            filterItems: [
                                {
                                    name: 'Да',
                                    value: 'Да',
                                },
                                { name: 'Нет', value: 'Нет' },
                            ],
                        },
                    },
                    {
                        field: 'isIncludedInWorkOrderChange',
                        headerName: 'Изм. к НЗ',
                        width: 95,
                        valueFormatter: ({ value }: { value: boolean }) => getYesNoStatus(value) ?? '',
                        filter: AgGridChoiceFilterAdapter,
                        filterParams: {
                            changeFilterHandler: handleTableFilterUpdated,
                            filterItems: [
                                {
                                    name: 'Да',
                                    value: 'Да',
                                },
                                { name: 'Нет', value: 'Нет' },
                            ],
                        },
                    },
                ],
            },
        ],
        [agreementId, checkedIds, filterChangeHandler, handleChangeCheckbox, handleTableFilterUpdated],
    );

    const handleClearSearch = useCallback(() => {
        if (searchString?.length) {
            setSearchString('');
            setQueryWhere((prevFilters: AnyObject) => ({
                ...prevFilters,
                searchString: undefined,
            }));
        }
    }, [searchString?.length]);

    const handleSearch = useCallback(({ value }) => {
        const trimValue = value?.trim() || null;

        setQueryWhere((prevState: AnyObject) => ({
            ...prevState,
            searchString: trimValue,
        }));
    }, []);
    const debouncedSearch = useMemo(() => debounce(handleSearch, 500), [handleSearch]);

    const handleChange = useCallback(
        ({ value }) => {
            setSearchString(value);
            debouncedSearch({ value });
        },
        [debouncedSearch],
    );

    useEffect(() => {
        setIsSubmittedDataTable(true);

        if (agreementUerForIncludingStatus === RequestStatus.SUCCESS && !agreementUerForIncludingErr) {
            setIsSubmittedDataTable(false);
        }
    }, [agreementUerForIncludingErr, agreementUerForIncludingStatus]);

    const handleCloseErrorAgreementTable = useCallback(() => setIsSubmittedDataTable(false), []);

    useEffect(() => {
        if (agreementUerForIncludingData?.uerPriceListUerForIncludingToContractChange?.items && getRowParams?.current) {
            getRowParams?.current?.successCallback(
                agreementUerForIncludingData.uerPriceListUerForIncludingToContractChange.items ?? [],
                agreementUerForIncludingData.uerPriceListUerForIncludingToContractChange?.totalCount,
            );
        }
    }, [
        getRowParams,
        agreementUerForIncludingData?.uerPriceListUerForIncludingToContractChange?.items,
        agreementUerForIncludingData?.uerPriceListUerForIncludingToContractChange?.totalCount,
    ]);

    useEffect(() => {
        if (!isModalOpened) {
            setQueryWhere({});
        }
    }, [isModalOpened]);

    return {
        titleModal: TITLE,
        searchPlaceholder: SEARCH_PLACEHOLDER,
        agreementId,
        checkedIds,
        searchString,
        agreementsData: agreementUerForIncludingData?.uerPriceListUerForIncludingToContractChange,
        agreementsStatus: agreementUerForIncludingStatus,
        agreementsErrors: agreementUerForIncludingErr,
        isSubmittedDataTable,
        handleTableFilterUpdated,
        handleSearch,
        handleChange,
        handleClearSearch,
        handleCloseErrorAgreementTable,
        finalColumns: columns,
        dataSource,
    };
};
